import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => ({ name: 'orders' }),
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/pedidos',
    name: 'orders',
    component: () => import('@/views/OrdersListView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/pedidos/:id',
    name: 'order-detail',
    component: () => import('@/views/OrderDetailView.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/pedidos/:id/scan',
    name: 'order-scan',
    component: () => import('@/views/ScannerView.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/embalaje',
    name: 'packing',
    component: () => import('@/views/PackingView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/embalaje/:id',
    name: 'packing-order',
    component: () => import('@/views/PackingOrderView.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, requiresSupervisor: true },
  },
  {
    path: '/equipo',
    name: 'team',
    component: () => import('@/views/TeamView.vue'),
    meta: { requiresAuth: true, requiresSupervisor: true },
  },
  {
    // Comodato: la máquina se entrega al cliente sin venderla. El depósito escanea la
    // serie y marca el despacho; el resto del circuito lo maneja Comercial en NetSuite.
    path: '/comodatos',
    name: 'comodatos',
    component: () => import('@/views/ComodatosView.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Salida en demostración: mercadería que sale a mostrar y vuelve en días. Comparte el
    // registro con comodato en NetSuite, pero va en pantalla aparte: la devolución usa
    // listas de condición distintas y mezclarlas manda el stock al destino equivocado.
    path: '/demostracion',
    name: 'demostracion',
    component: () => import('@/views/DemostracionView.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Devoluciones de e-commerce: se escanea el rótulo del paquete y la orden de
    // devolución aparece sola. Siempre entra a DEVOLUCIONES, nunca al stock que se publica.
    path: '/devoluciones',
    name: 'devoluciones',
    component: () => import('@/views/DevolucionesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/envios',
    name: 'shipping',
    component: () => import('@/views/ShippingView.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Despachar pasando el QR de cada caja, como la colecta de Mercado Libre.
    path: '/despacho-scan',
    name: 'despacho-scan',
    component: () => import('@/views/DespachoScanView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/mis-stats',
    name: 'my-stats',
    component: () => import('@/views/MyStatsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/eficiencia',
    name: 'efficiency',
    component: () => import('@/views/EficienciaView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/consent/gps',
    name: 'gps-consent',
    component: () => import('@/views/GpsConsentView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/traspasos/nuevo',
    name: 'transfer-create',
    component: () => import('@/views/TransferCreateView.vue'),
    meta: { requiresAuth: true, requiresSupervisor: true },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: () => ({ name: 'orders' }),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.hydrated) await auth.restore();

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'orders' };
  }
  if (to.meta.requiresSupervisor && !auth.isSupervisor) {
    return { name: 'orders' };
  }
  // Primer login: si todavía no dio consent de GPS y la feature está on, mandamos a la pantalla.
  if (
    auth.isAuthenticated &&
    !auth.employee?.gps_consent_at &&
    to.name !== 'gps-consent' &&
    to.name !== 'login'
  ) {
    return { name: 'gps-consent' };
  }
  return true;
});

export default router;
